import { inject, Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, shareReplay } from 'rxjs';

import { Address } from '@spartacus/core';

import {
  getCodeDetailFromAddressData, isAddressValid, isEmailValid, isNotSureAddress, isPhoneNumberValid, isValidEmailFormat
} from '../utils';
import { DqeService } from './dqe.service';
import { DynamicFormService } from './dynamic-form.service';
import { CustomPatternValidator } from '../constants/validators';
import { CustomAddressForm } from '../models/custom-address.model';
import { AddressValidationResult } from '../models/address-validation-result.model';
import { BaseStoreService } from './base-store.service';

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private readonly dqeService = inject(DqeService);
  private readonly baseStoreService = inject(BaseStoreService);
  private readonly dynamicFormService = inject(DynamicFormService);

  private readonly addressFormCache = new Map<string, Observable<CustomAddressForm>>();

  validateAddress(
    address: Address,
    isDqeValidationEnabled: boolean,
    isExpressCheckoutEnabled: boolean
  ): Observable<AddressValidationResult> {
    return isDqeValidationEnabled
      ? this.validateWithDqe(address)
      : this.validateWithoutDqe(address, isExpressCheckoutEnabled);
  }

  private get countryIsoCode() {
    return this.baseStoreService.baseStore.market;
  }

  private validateWithDqe(address: Address): Observable<AddressValidationResult> {
    const result: AddressValidationResult = {
      isValid: false
    };

    const validations: Observable<any>[] = [];

    if (address?.email) {
      validations.push(
        this.dqeService.validate(address.email).pipe(
          map((response) => result.emailValid = isEmailValid(response) && isValidEmailFormat(address.email)),
          catchError(() => {
            result.emailValid = false;
            return of(null);
          })
        )
      );
    }

    if (address?.phone) {
      validations.push(
        this.dqeService.validateTel(address.phone, 'phone').pipe(
          map((telDataEligibility) => result.phoneValid = isPhoneNumberValid(telDataEligibility)),
          catchError(() => {
            result.phoneValid = false;
            return of(null);
          })
        )
      );
    }

    if (address?.phone2) {
      validations.push(
        this.dqeService.validateTel(address.phone2, 'phone2').pipe(
          map((telDataEligibility) => result.phone2Valid = isPhoneNumberValid(telDataEligibility)),
          catchError(() => {
            result.phone2Valid = false;
            return of(null);
          })
        )
      );
    }

    if (address?.streetName && address?.postalCode && address?.town) {
      validations.push(
        this.dqeService.validateAddress(address.streetName, address.postalCode, address.town).pipe(
          map((addressDataEligibility) => {
            const codeDetail = getCodeDetailFromAddressData(addressDataEligibility);

            result.addressValid = isAddressValid(codeDetail) && !isNotSureAddress(codeDetail);
          }),
          catchError(() => {
            result.addressValid = false;
            return of(null);
          })
        )
      );
    }

    if (validations.length === 0) {
      result.isValid = false;
      return of(result);
    }

    return combineLatest(validations).pipe(
      map(() => {
        result.isValid = [
          result.emailValid,
          result.phoneValid,
          result.phone2Valid,
          result.addressValid
        ]
          .filter((v) => v !== undefined)
          .every((v) => v === true);
        return result;
      })
    );
  }

  private validateWithoutDqe(address: Address, isExpressCheckoutEnabled: boolean): Observable<AddressValidationResult> {
    return this.getCachedAddressFormDefByCountry().pipe(
      map((formDef) => {
        const validatorsMap = this.extractValidatorsMap(formDef);
        const emailValid = !address?.email || this.isValidEmail(address.email);
        const phoneValid = isExpressCheckoutEnabled ? true : !address?.phone || this.validateWithRegex(address.phone, validatorsMap['phone']);
        const phone2Valid = isExpressCheckoutEnabled ? true : !address?.phone2 || this.validateWithRegex(address.phone2, validatorsMap['phone2']);
        const postalCodeValid = !address?.postalCode || this.validateWithRegex(address.postalCode, validatorsMap['postalCode']);
        const addressValid = !!(address?.streetName && address?.postalCode && address?.town);

        const isValid = [emailValid, phoneValid, phone2Valid, postalCodeValid, addressValid].every((value) => value === true);

        return {
          isValid,
          emailValid,
          phoneValid,
          phone2Valid,
          postalCodeValid,
          addressValid
        };
      })
    );
  }

  private getCachedAddressFormDefByCountry(): Observable<CustomAddressForm> {
    if (!this.addressFormCache.has(this.countryIsoCode)) {
      const cached$ = this.dynamicFormService.getAddressFormByCountry().pipe(
        shareReplay(1)
      );
      this.addressFormCache.set(this.countryIsoCode, cached$);
    }

    return this.addressFormCache.get(this.countryIsoCode);
  }

  private extractValidatorsMap(formDef: any): Record<string, string> {
    const validators: Record<string, string> = {};

    formDef.formAttributeSections?.forEach((section) => {
      section.formAttributes?.forEach((attr) => {
        const name = attr.originalName ?? attr.name;
        if (attr.regexp && name) {
          validators[name] = attr.regexp;
        }
      });
    });

    return validators;
  }

  private validateWithRegex(value: string, pattern?: string): boolean {
    if (!pattern) return true;
    const regex = new RegExp(pattern);
    return regex.test(value?.trim());
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = new RegExp(CustomPatternValidator.email);

    return emailPattern.test(email);
  }
}
